/**
 * @fileoverview Error Handling Service - Sistema Centralizado de Tratamento de Erros
 * @description Substitui Firebase Error Handling com PostgreSQL + API REST
 *
 * FUNCIONALIDADES:
 * ✅ Tratamento centralizado de erros HTTP
 * ✅ Categorização automática de erros
 * ✅ Notificações toast personalizadas
 * ✅ Log de erros para auditoria
 * ✅ Retry automático para falhas temporárias
 * ✅ Fallback para operações offline
 * ✅ Métricas de erro em tempo real
 *
 * @author GestorConsig Team
 * @version 1.0.0
 * @created 27/12/2024
 */

import { api } from '@/store/api'
// import config from '@/config/environment' // Removido: não utilizado

class ErrorHandlingService {
  constructor() {
    this.errorQueue = []
    this.errorStats = {
      total: 0,
      byType: {},
      byComponent: {},
      lastError: null
    }
    this.retryConfig = {
      maxRetries: 3,
      retryDelay: 1000,
      retryableStatuses: [408, 429, 500, 502, 503, 504]
    }
    this.offlineQueue = []
    this.isOnline = navigator.onLine

    // Monitorar conectividade
    this.setupConnectivityMonitoring()
  }

  /**
   * 🔥 TRATAMENTO PRINCIPAL DE ERROS
   * Substitui Firebase error handling
   */
  handleError(error, context = {}) {
    try {
      // Categorizar erro
      const errorInfo = this.categorizeError(error, context)

      // Atualizar estatísticas
      this.updateErrorStats(errorInfo)

      // Log do erro
      this.logError(errorInfo)

      // Determinar ação
      const action = this.determineAction(errorInfo)

      // Executar ação
      this.executeAction(action, errorInfo)

      return errorInfo
    } catch (handlingError) {
      console.error('❌ Erro no tratamento de erro:', handlingError)
      this.showFallbackError()
    }
  }

  /**
   * 🏷️ CATEGORIZAR ERRO
   */
  categorizeError(error, context) {
    const errorInfo = {
      id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      context,
      original: error,
      type: 'unknown',
      severity: 'medium',
      category: 'general',
      message: 'Erro desconhecido',
      userMessage: 'Ocorreu um erro inesperado',
      action: 'show_toast',
      retryable: false,
      metadata: {}
    }

    // 🌐 ERROS DE REDE
    if (!error.response && error.request) {
      errorInfo.type = 'network'
      errorInfo.category = 'connectivity'
      errorInfo.severity = 'high'
      errorInfo.message = 'Erro de conectividade'
      errorInfo.userMessage = 'Problema de conexão. Verifique sua internet.'
      errorInfo.retryable = true
      errorInfo.action = 'show_toast_retry'
    } else if (error.response) {
      // 📡 ERROS HTTP
      const status = error.response.status
      const data = error.response.data || {}

      errorInfo.type = 'http'
      errorInfo.metadata.status = status
      errorInfo.metadata.url = error.config?.url
      errorInfo.metadata.method = error.config?.method

      switch (Math.floor(status / 100)) {
        case 4: // 4xx - Erro do cliente
          errorInfo.category = 'client'
          errorInfo.severity =
            status === 401 || status === 403 ? 'high' : 'medium'
          errorInfo.retryable = status === 429 // Rate limiting

          if (status === 400) {
            errorInfo.message = 'Dados inválidos'
            errorInfo.userMessage =
              data.error || 'Dados inválidos. Verifique as informações.'
          } else if (status === 401) {
            // Pegamos a URL original que causou o erro 401
            const originalUrl = error.config?.url || ''

            // Verificamos se o 401 veio da rota de login
            if (originalUrl.includes('/auth/login')) {
              // SE VEIO DO LOGIN: Não redirecione, apenas mostre o erro.
              // O SignIn.vue vai capturar isso.
              errorInfo.message = 'Não autorizado (Login)'
              errorInfo.userMessage = data.error || 'Credenciais inválidas'
              errorInfo.action = 'show_toast' // Ação padrão
              errorInfo.severity = 'medium' // É um erro de input, não de sessão
            } else {
              // SE VEIO DE OUTRA ROTA: É token expirado. Redirecione.
              errorInfo.message = 'Não autorizado'
              errorInfo.userMessage = 'Sessão expirada. Faça login novamente.'
              errorInfo.action = 'redirect_login'
            }
          } else if (status === 403) {
            errorInfo.message = 'Acesso negado'
            errorInfo.userMessage =
              'Você não tem permissão para esta operação.'
          } else if (status === 404) {
            errorInfo.message = 'Recurso não encontrado'
            errorInfo.userMessage = 'Recurso não encontrado.'
          } else if (status === 429) {
            errorInfo.message = 'Muitas requisições'
            errorInfo.userMessage = 'Muitas tentativas. Aguarde um momento.'
            errorInfo.retryable = true
          }
          break

        case 5: // 5xx - Erro do servidor
          errorInfo.category = 'server'
          errorInfo.severity = 'high'
          errorInfo.retryable = true
          errorInfo.message = 'Erro interno do servidor'
          errorInfo.userMessage = 'Erro interno. Tente novamente em instantes.'
          break
      }
      // Usar mensagem específica da API se disponível
      // MOVEMOS A ATRIBUIÇÃO DE MENSAGEM PARA DENTRO DOS BLOCOS 4xx
      // if (data.error && typeof data.error === 'string') {
      //  errorInfo.userMessage = data.error
      // }
    } else if (error.name === 'ValidationError' || error.details) {
      // 📋 ERROS DE VALIDAÇÃO
      errorInfo.type = 'validation'
      errorInfo.category = 'form'
      errorInfo.severity = 'low'
      errorInfo.message = 'Erro de validação'
      errorInfo.userMessage = 'Verifique os dados do formulário.'
      errorInfo.metadata.validationErrors = error.details
    } else if (error.name === 'JsonWebTokenError') {
      // 🔐 ERROS DE AUTENTICAÇÃO JWT
      errorInfo.type = 'auth'
      errorInfo.category = 'security'
      errorInfo.severity = 'high'
      errorInfo.message = 'Token inválido'
      errorInfo.userMessage = 'Sessão inválida. Faça login novamente.'
      errorInfo.action = 'redirect_login'
    } else if (error.code === 'ECONNABORTED') {
      // ⏱️ ERROS DE TIMEOUT
      errorInfo.type = 'timeout'
      errorInfo.category = 'performance'
      errorInfo.severity = 'medium'
      errorInfo.retryable = true
      errorInfo.message = 'Timeout na requisição'
      errorInfo.userMessage = 'Operação demorou muito. Tente novamente.'
    } else if (error instanceof Error) {
      // 🔧 ERROS DE APLICAÇÃO
      errorInfo.type = 'application'
      errorInfo.category = 'logic'
      errorInfo.severity = 'medium'
      errorInfo.message = error.message
      errorInfo.userMessage = 'Erro na aplicação. Tente recarregar a página.'
    }

    return errorInfo
  }

  /**
   * 🎯 DETERMINAR AÇÃO BASEADA NO ERRO
   */
  determineAction(errorInfo) {
    // Ações predefinidas
    if (errorInfo.action && errorInfo.action !== 'show_toast') {
      return errorInfo.action
    }

    // Lógica de decisão
    if (errorInfo.severity === 'high' && errorInfo.type === 'auth') {
      return 'redirect_login'
    }

    if (errorInfo.retryable && errorInfo.severity !== 'low') {
      return 'show_toast_retry'
    }

    if (errorInfo.type === 'validation') {
      return 'show_validation_errors'
    }

    if (!this.isOnline) {
      return 'queue_offline'
    }

    return 'show_toast'
  }

  /**
   * ⚡ EXECUTAR AÇÃO
   */
  executeAction(action, errorInfo) {
    switch (action) {
      case 'show_toast':
        this.showToastError(errorInfo)
        break

      case 'show_toast_retry':
        this.showToastWithRetry(errorInfo)
        break

      case 'show_validation_errors':
        this.showValidationErrors(errorInfo)
        break

      case 'redirect_login':
        this.redirectToLogin(errorInfo)
        break

      case 'queue_offline':
        this.queueForOffline(errorInfo)
        break

      case 'silent':
        // Log apenas, sem notificação
        break

      default:
        this.showToastError(errorInfo)
    }
  }

  /**
   * 🍞 MOSTRAR TOAST DE ERRO
   */
  showToastError(errorInfo) {
    if (typeof window === 'undefined' || !window.$snotify) {
      return
    }

    const config = {
      timeout: this.getTimeoutByType(errorInfo.type),
      showProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true
    }

    // Adicionar botão de detalhes para erros complexos em modo debug
    if (process.env.NODE_ENV === 'development' && errorInfo.metadata) {
      config.buttons = [
        {
          text: 'Detalhes',
          action: () => {
            console.group('🔍 Detalhes do Erro')
            console.log('Error Info:', errorInfo)
            console.log('Original Error:', errorInfo.original)
            console.groupEnd()
          }
        }
      ]
    }

    window.$snotify.error(errorInfo.userMessage, config)
  }

  /**
   * 🔄 MOSTRAR TOAST COM RETRY
   */
  showToastWithRetry(errorInfo) {
    if (typeof window === 'undefined' || !window.$snotify) {
      return
    }

    const config = {
      timeout: 0, // Não remove automaticamente
      showProgressBar: false,
      closeOnClick: false,
      buttons: [
        {
          text: 'Tentar Novamente',
          action: async() => {
            try {
              await this.retryOperation(errorInfo)
              window.$snotify.success('Operação realizada com sucesso!')
            } catch (retryError) {
              this.handleError(retryError, {
                ...(errorInfo.context || {}),
                isRetry: true
              })
            }
          }
        },
        {
          text: 'Dispensar',
          action: () => true // Fecha o toast
        }
      ]
    }

    window.$snotify.error(
      `${errorInfo.userMessage} Deseja tentar novamente?`,
      config
    )
  }

  /**
   * 📋 MOSTRAR ERROS DE VALIDAÇÃO
   */
  showValidationErrors(errorInfo) {
    if (typeof window === 'undefined' || !window.$snotify) {
      return
    }

    if (errorInfo.metadata.validationErrors) {
      const errors = errorInfo.metadata.validationErrors
      const errorMessages = errors.map((e) => `• ${e.message}`).join('\n')

      window.$snotify.warning(`Corrija os seguintes erros:\n${errorMessages}`, {
        timeout: 8000,
        showProgressBar: true,
        closeOnClick: true
      })
    } else {
      this.showToastError(errorInfo)
    }
  }

  /**
   * 🔐 REDIRECIONAR PARA LOGIN
   */
  redirectToLogin(errorInfo) {
    // Limpar dados de autenticação
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_data')
    }

    // Mostrar notificação
    if (typeof window !== 'undefined' && window.$snotify) {
      window.$snotify.warning(errorInfo.userMessage, {
        timeout: 3000,
        showProgressBar: true
      })
    }

    // Redirecionar
    setTimeout(() => {
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login'
      ) {
        window.location.href = '/login'
      }
    }, 1000)
  }

  /**
   * 📦 QUEUE PARA OPERAÇÕES OFFLINE
   */
  queueForOffline(errorInfo) {
    this.offlineQueue.push({
      ...errorInfo,
      queuedAt: new Date().toISOString()
    })

    if (typeof window !== 'undefined' && window.$snotify) {
      window.$snotify.info(
        'Sem conexão. Operação será executada quando voltar online.',
        {
          timeout: 5000,
          showProgressBar: true
        }
      )
    }
  }

  /**
   * 🔄 RETRY DE OPERAÇÃO
   */
  async retryOperation(errorInfo) {
    const { context } = errorInfo

    if (!context.originalRequest) {
      throw new Error('Não é possível repetir esta operação')
    }

    // Implementar retry com backoff exponencial
    let lastError
    for (let attempt = 1; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        const delay = this.retryConfig.retryDelay * Math.pow(2, attempt - 1)
        await new Promise((resolve) => setTimeout(resolve, delay))

        return await api(context.originalRequest)
      } catch (error) {
        lastError = error
        if (attempt === this.retryConfig.maxRetries) {
          break
        }
      }
    }

    throw lastError
  }

  /**
   * 📊 ATUALIZAR ESTATÍSTICAS
   */
  updateErrorStats(errorInfo) {
    this.errorStats.total++
    this.errorStats.lastError = errorInfo

    // Por tipo
    if (!this.errorStats.byType[errorInfo.type]) {
      this.errorStats.byType[errorInfo.type] = 0
    }
    this.errorStats.byType[errorInfo.type]++

    // Por componente
    const component = (errorInfo.context && errorInfo.context.component) || 'unknown'
    if (!this.errorStats.byComponent[component]) {
      this.errorStats.byComponent[component] = 0
    }
    this.errorStats.byComponent[component]++
  }

  /**
   * 📝 LOG DE ERRO
   */
  logError(errorInfo) {
    // Log local
    console.group(`🔥 Error [${errorInfo.type}] - ${errorInfo.severity}`)
    console.error('Message:', errorInfo.message)
    console.error('User Message:', errorInfo.userMessage)
    console.error('Context:', errorInfo.context)
    console.error('Original:', errorInfo.original)
    console.groupEnd()

    // Enviar para servidor (não bloquear se falhar)
    this.sendErrorToServer(errorInfo).catch((err) => {
      console.warn('⚠️ Falha ao enviar erro para servidor:', err)
    })
  }

  /**
   * 📡 ENVIAR ERRO PARA SERVIDOR
   */
  async sendErrorToServer(errorInfo) {
    try {
      // 🚨 EVITAR LOOP INFINITO: Usar rota pública para erros de autenticação
      const isAuthError = errorInfo.type === 'auth' ||
                         (errorInfo.type === 'http' &&
                          (errorInfo.metadata?.status === 401 || errorInfo.metadata?.status === 403))

      if (isAuthError) {
        // Usar rota pública para erros de autenticação
        console.warn('⚠️ Enviando erro de autenticação via rota pública')
        await this.sendPublicError(errorInfo)
        return
      }

      // Verificar se há token válido antes de tentar enviar
      const token = localStorage.getItem('auth_token')
      if (!token) {
        console.warn('⚠️ Sem token de autenticação - usando rota pública')
        await this.sendPublicError(errorInfo)
        return
      }

      // Não enviar dados sensíveis
      const sanitizedError = {
        id: errorInfo.id,
        timestamp: errorInfo.timestamp,
        type: errorInfo.type,
        category: errorInfo.category,
        severity: errorInfo.severity,
        message: errorInfo.message,
        metadata: {
          ...errorInfo.metadata,
          userAgent: navigator.userAgent,
          url: window.location.href,
          component: (errorInfo.context && errorInfo.context.component) || 'unknown'
        }
      }

      await api.post('/errors/log', sanitizedError)
    } catch (error) {
      // Se falhar, tentar rota pública como fallback
      console.warn('⚠️ Falha na rota autenticada, tentando rota pública')
      try {
        await this.sendPublicError(errorInfo)
      } catch (publicError) {
        console.warn('⚠️ Falha completa ao enviar erro para servidor')
      }
    }
  }

  /**
   * 📡 ENVIAR ERRO VIA ROTA PÚBLICA
   */
  async sendPublicError(errorInfo) {
    try {
      // Usar axios diretamente para evitar interceptors
      const axios = require('axios')

      const sanitizedError = {
        id: errorInfo.id,
        timestamp: errorInfo.timestamp,
        type: errorInfo.type,
        category: errorInfo.category,
        severity: errorInfo.severity,
        message: errorInfo.message,
        metadata: {
          ...errorInfo.metadata,
          userAgent: navigator.userAgent,
          url: window.location.href,
          component: (errorInfo.context && errorInfo.context.component) || 'unknown',
          public_route: true
        }
      }

      const baseURL = process.env.VUE_APP_API_URL || 'http://localhost:3000/api'
      await axios.post(`${baseURL}/errors/log/public`, sanitizedError, {
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json'
        }
      })
    } catch (error) {
      // Falha silenciosa para evitar loops
      console.warn('⚠️ Falha silenciosa ao enviar erro via rota pública')
    }
  }

  /**
   * 🌐 MONITORAR CONECTIVIDADE
   */
  setupConnectivityMonitoring() {
    window.addEventListener('online', () => {
      this.isOnline = true
      this.processOfflineQueue()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
    })
  }

  /**
   * 📦 PROCESSAR QUEUE OFFLINE
   */
  async processOfflineQueue() {
    if (this.offlineQueue.length === 0) return

    if (typeof window !== 'undefined' && window.$snotify) {
      window.$snotify.info(
        'Conexão restaurada. Processando operações pendentes...'
      )
    }

    const queue = [...this.offlineQueue]
    this.offlineQueue = []

    for (const errorInfo of queue) {
      try {
        await this.retryOperation(errorInfo)
      } catch (error) {
        // Re-adicionar à queue se ainda falhar
        this.offlineQueue.push(errorInfo)
      }
    }

    if (
      this.offlineQueue.length === 0 &&
      typeof window !== 'undefined' &&
      window.$snotify
    ) {
      window.$snotify.success('Todas as operações foram processadas!')
    }
  }

  /**
   * 🔧 UTILITÁRIOS
   */
  generateErrorId() {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  getTimeoutByType(type) {
    const timeouts = {
      network: 8000,
      auth: 6000,
      validation: 6000,
      server: 5000,
      application: 4000
    }
    return timeouts[type] || 4000
  }

  showFallbackError() {
    if (typeof window !== 'undefined' && window.$snotify) {
      window.$snotify.error('Erro crítico na aplicação. Recarregue a página.', {
        timeout: 10000,
        buttons: [
          {
            text: 'Recarregar',
            action: () => window.location.reload()
          }
        ]
      })
    }
  }

  /**
   * 📊 OBTER ESTATÍSTICAS
   */
  getStats() {
    return {
      ...this.errorStats,
      queueSize: this.offlineQueue.length,
      isOnline: this.isOnline
    }
  }

  /**
   * 🧹 LIMPAR ESTATÍSTICAS
   */
  clearStats() {
    this.errorStats = {
      total: 0,
      byType: {},
      byComponent: {},
      lastError: null
    }
  }
}

// 🌍 INSTÂNCIA GLOBAL
const errorHandlingService = new ErrorHandlingService()

// 🔌 INTEGRAÇÃO COM VUE
export function installErrorHandler(Vue) {
  // Handler global para erros Vue
  Vue.config.errorHandler = (err, vm, info) => {
    errorHandlingService.handleError(err, {
      component: (vm && vm.$options && vm.$options.name) || 'Unknown',
      info,
      type: 'vue'
    })
  }

  // Adicionar ao prototype
  Vue.prototype.$handleError = (error, context = {}) => {
    return errorHandlingService.handleError(error, context)
  }

  Vue.prototype.$errorStats = () => {
    return errorHandlingService.getStats()
  }
}

// 📤 EXPORTAÇÕES
export default errorHandlingService
export { ErrorHandlingService }
